import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useGenerateDialogPath } from '@sorare/core/src/hooks/navigation/useGenerateDialogPath';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useTokenOfferBelongsToUser } from '@sorare/core/src/hooks/useTokenOfferBelongsToUser';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';

import { TokenDetailsRoot } from 'components/ItemPreview/ui';

import { SalePrice } from '../SalePrice';
import { SaleWinner } from '../SaleWinner';
import type {
  EndedSaleDetails_anyCard,
  EndedSaleDetails_offer,
} from './__generated__/index.graphql';

type Props = {
  sale: EndedSaleDetails_offer;
  card: EndedSaleDetails_anyCard;
};

export const EndedSaleDetails = ({ sale, card }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const belongsToUser = useTokenOfferBelongsToUser();
  const track = useEvents();
  const { isIosApp, isMarketAuthorizedIosApp } = useIsMobileApp();

  const ownsToken = currentUser?.slug === card.user?.slug;
  const offerExpired = sale.status === 'ended';
  const relistedToken =
    card.myMintedSingleSaleOffer && belongsToUser(card.myMintedSingleSaleOffer);

  const onClickListAgain = () => {
    track('Click List Again', { cardSlug: card.slug });
  };

  const generateDialogPath = useGenerateDialogPath();
  const dialogPath = generateDialogPath(DialogKey.singleSaleOffer, card.slug, {
    additionalParams: {
      previousSaleOffer: sale.id,
    },
  });

  return (
    <TokenDetailsRoot>
      <SalePrice sale={sale} showFees={!!sale.acceptedAt} />
      {offerExpired ? (
        <LabelS color="var(--c-nd-600)">
          <FormattedMessage
            id="MySorare.PastSaleOfferItem.Expired"
            defaultMessage="Expired"
          />
        </LabelS>
      ) : (
        <SaleWinner sale={sale} />
      )}
      {!!ownsToken && !!offerExpired && (
        <div>
          {relistedToken ? (
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage
                id="MySorare.PastSaleOfferItem.Relisted"
                defaultMessage="Currently re-listed"
              />
            </LabelM>
          ) : (
            <Button
              disabled={!!isIosApp && !isMarketAuthorizedIosApp}
              size="small"
              color="primary"
              to={dialogPath}
              onClick={onClickListAgain}
            >
              <FormattedMessage
                id="MySorare.PastSaleOfferItem.ListAgain"
                defaultMessage="List Again"
              />
            </Button>
          )}
        </div>
      )}
    </TokenDetailsRoot>
  );
};

EndedSaleDetails.fragments = {
  anyCard: gql`
    fragment EndedSaleDetails_anyCard on AnyCardInterface {
      slug
      user {
        slug
      }
      myMintedSingleSaleOffer @skip(if: $onlyPrimary) {
        id
        ...useTokenOfferBelongsToUser_offer
      }
    }
    ${useTokenOfferBelongsToUser.fragments.offer}
  ` as TypedDocumentNode<EndedSaleDetails_anyCard>,
  offer: gql`
    fragment EndedSaleDetails_offer on TokenOffer {
      id
      status
      acceptedAt
      ...SaleWinner_offer
      ...SalePrice_offer
      ...useTokenOfferBelongsToUser_offer
    }
    ${useTokenOfferBelongsToUser.fragments.offer}
    ${SaleWinner.fragments.offer}
    ${SalePrice.fragments.offer}
  ` as TypedDocumentNode<EndedSaleDetails_offer>,
};
